/** @format */

import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import MvnoOperationSetup, { MvnoOperationSetupCollectionInterface } from '../../../interfaces/platform/MvnoOperatorSetup';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import PlatformResellerMvnoOperationSetup, {
    PlatformResellerMvnoOperationSetupCollectionInterface,
} from '../../../interfaces/platform/PlatformResellerMvnoOperationSetup';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import Card from '../../../components/Card/Card';
import CardBody from '../../../components/Card/CardBody';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { useOperatorServiceUseType } from '../../../modules/lib/hooks/useOperatorType';
import PlatformResellerInterface from '../../../interfaces/platform/PlatformResellerInterface';

const _ = require('underscore');

type PlatformResellerMvnoOperatorSetupType = {
    platformResellerId: string | null;
    reload: () => void;
};

export const PlatformResellerMvnoOperatorSetup: FunctionComponent<PlatformResellerMvnoOperatorSetupType> = ({
    platformResellerId,
    reload,
}): ReactElement => {
    const dispatch = useDispatch();
    const [platformReseller, setPlatformReseller] = useState<PlatformResellerInterface | null>(null);
    const [mvnoOperatorSetup, setMvnoOperatorSetup] = useState<MvnoOperationSetupCollectionInterface | null>(null);
    const [
        platformResellerMvnoOperationSetup,
        setPlatformResellerMvnoOperationSetup,
    ] = useState<PlatformResellerMvnoOperationSetupCollectionInterface | null>(null);
    const serviceUseType = useOperatorServiceUseType();

    const onChangeSelectEvent = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const field = event.target.name;
        const selected = parseInt(event.target.value);
        if (platformReseller) {
            setPlatformReseller({ ...platformReseller, useType: selected });
        }
    };

    useEffect(() => {
        if (platformReseller === null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getWithCallback(
                FormGeneralHelper.getByUri(platformResellerId ?? '-1'),
                'get_platform_reseller',
                (data: PlatformResellerInterface) => {
                    setPlatformReseller(data);
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        }
    }, [dispatch, platformReseller, platformResellerId]);

    useEffect(() => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/mvno_operator_setups')),
            'MVNO_OPERATOR_SETUP_GET_FOR_RESELLER',
            [
                { value: 'TARIFF', name: 'typeValue' },
                { name: 'operatorType', value: 2 },
            ],
            [{ name: 'displayName', value: 'asc' }],
            (data: MvnoOperationSetupCollectionInterface): void => {
                setMvnoOperatorSetup(data);
            },
            { dispatchAfterCallback: false, propagateDataToCallback: true }
        );
    }, [dispatch]);

    useEffect(() => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/platform_reseller_mvno_operator_setups')),
            'MVNO_OPERATOR_SETUP_GET_FOR_RESELLER_COLLECTION',
            [
                { value: platformResellerId ? platformResellerId.replace(/adescom-api/, 'base-api') : -1, name: 'platformReseller' },
                { name: 'statusFlag[lt]', value: 10 },
            ],
            [],
            (data: PlatformResellerMvnoOperationSetupCollectionInterface): void => {
                setPlatformResellerMvnoOperationSetup(data);
            },
            { dispatchAfterCallback: false, propagateDataToCallback: true }
        );
    }, [platformResellerId, dispatch]);

    const checkProfile = (profile: MvnoOperationSetup): boolean => {
        if (profile.enabled !== undefined) {
            return profile.enabled ?? false;
        }
        if (platformResellerMvnoOperationSetup && mvnoOperatorSetup) {
            for (let i = 0; i < platformResellerMvnoOperationSetup['hydra:member'].length; i++) {
                if (
                    platformResellerMvnoOperationSetup['hydra:member'][i].mvnoOperatorSetup.replace(/base-api/, 'adescom-api') === profile['@id'] &&
                    platformResellerMvnoOperationSetup['hydra:member'][i].statusFlag === 1
                ) {
                    return true;
                }
            }
        }
        return false;
    };

    const onCheckBoxEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const name = event.target.name;
        const value = event.target.value;
        const checked = event.target.checked;
        const local: MvnoOperationSetupCollectionInterface = _.clone(mvnoOperatorSetup);
        for (let i = 0; i < local['hydra:member'].length; i++) {
            if (local['hydra:member'][i].constValue === value) {
                local['hydra:member'][i].enabled = checked;
            }
        }
        setMvnoOperatorSetup(local);
    };

    const save = (): void => {
        const itemLoader = new ItemLoaders(dispatch);
        if (!mvnoOperatorSetup || !platformResellerMvnoOperationSetup) {
            return;
        }
        for (let i = 0; i < mvnoOperatorSetup['hydra:member'].length; i++) {
            if (mvnoOperatorSetup['hydra:member'][i].enabled !== undefined) {
                itemLoader.postWithCallback(
                    FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/platform_reseller_mvno_operator_setups')),
                    'MVNO_OPERATOR_SETUP_POST_FOR_RESELLER_COLLECTION',
                    {
                        platformReseller: platformResellerId?.replace(/adescom-api/, 'base-api'),
                        mvnoOperatorSetup: mvnoOperatorSetup['hydra:member'][i]['@id'].replace(/adescom-api/, 'base-api'),
                        statusFlag: mvnoOperatorSetup['hydra:member'][i].enabled ? 1 : 10,
                    } as PlatformResellerMvnoOperationSetup,
                    (data: PlatformResellerMvnoOperationSetup): void => {},
                    { propagateDataToCallback: true, dispatchAfterCallback: false }
                );
            }
        }
        itemLoader.putWithCallback(
            FormGeneralHelper.getByUri(platformResellerId ?? '-1'),
            'put_platform_reseller',
            platformReseller,
            () => {
                reload();
            },
            {
                dispatchAfterCallback: false,
                propagateDataToCallback: true,
            }
        );
    };

    return (
        <Card>
            <CardHeader trans="platform_reseller:h.allowed_profiles" />
            <CardBody>
                {platformReseller && mvnoOperatorSetup && platformResellerMvnoOperationSetup && (
                    <GridContainer>
                        {/*<GridItem xs={6} style={{ paddingTop: '8px' }}>*/}
                        {/*    <SimpleSelect*/}
                        {/*        label="platform_reseller:h.use_type"*/}
                        {/*        fieldName="useType"*/}
                        {/*        required={true}*/}
                        {/*        elements={serviceUseType}*/}
                        {/*        dispatchName={'platform_reseller_use_type'}*/}
                        {/*        selectedValue={platformReseller.useType}*/}
                        {/*        callback={onChangeSelectEvent}*/}
                        {/*    />*/}
                        {/*</GridItem>*/}
                        <GridItem xs={6} />
                        {mvnoOperatorSetup['hydra:member'].map((profile) => {
                            return (
                                <GridItem xs={6} key={'profile_' + profile.id}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={checkProfile(profile)}
                                                name={profile.constName}
                                                onChange={onCheckBoxEvent}
                                                value={profile.constValue}
                                            />
                                        }
                                        label={profile.displayName}
                                    />
                                </GridItem>
                            );
                        })}
                        <GridItem xs={10} />
                        <GridItem xs={2}>
                            <CustomTextButtom
                                callback={(): void => {
                                    save();
                                }}
                                color={'primary'}
                                size={'lg'}
                                label={'general:common.save'}
                            />
                        </GridItem>
                    </GridContainer>
                )}
            </CardBody>
        </Card>
    );
};
